import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { MessagesSquare, RefreshCw, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function AskLeoView({ scene }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [history, setHistory] = useState(scene.ask_leo_history || []);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history.length]);

  const send = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/ask-leo`, { message: input });
      setHistory(r.data.history);
      setInput("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("reh.send_fail"));
    } finally { setSending(false); }
  };

  const reset = async () => {
    if (!window.confirm(t("ask.confirm_reset"))) return;
    try {
      await api.post(`/scenes/${scene.scene_id}/ask-leo/reset`);
      setHistory([]);
    } catch { toast.error(t("reh.reset_fail")); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-amber-500">{t("ask.eyebrow")}</div>
          <h2 className="serif text-3xl text-stone-100 mt-1">{t("ask.title")}</h2>
          <p className="text-stone-400 text-sm mt-2 max-w-xl">{t("ask.subtitle")}</p>
        </div>
        <button data-testid="ask-reset-btn" onClick={reset} className="ghost-btn !py-2 !text-xs"><RefreshCw size={12}/> {t("ask.reset")}</button>
      </div>

      <div className="paper p-0 mt-6 flex flex-col max-h-[65vh]">
        <div translate="no" data-testid="leo-transcript" className="notranslate flex-1 overflow-y-auto p-5 space-y-4">
          {history.length === 0 && (
            <div className="text-center py-8">
              <MessagesSquare className="mx-auto text-stone-600" size={32}/>
              <div className="text-stone-500 text-sm mt-3">{t("ask.empty")}</div>
            </div>
          )}
          <AnimatePresence initial={false}>
            {history.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`ask-msg-${m.role}-${i}`}
                className={`${m.role === "leo" ? "" : "text-right"}`}
              >
                <div className={`mono text-[10px] tracking-widest uppercase ${m.role === "leo" ? "text-amber-500/70" : "text-stone-500"}`}>
                  {m.role === "leo" ? t("ask.leo") : t("ask.you")}
                </div>
                <div className={`mt-1 whitespace-pre-wrap leading-relaxed ${
                  m.role === "leo"
                    ? "text-stone-200 serif text-lg"
                    : "text-stone-300 mono text-[14px] inline-block bg-stone-900/60 px-3 py-2 rounded-sm"
                }`}>
                  {m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={bottomRef}/>
        </div>
        <div className="border-t border-stone-800 p-4 flex gap-2">
          <input
            data-testid="ask-input"
            value={input}
            onChange={(e)=>setInput(e.target.value)}
            onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
            className="script-input flex-1 text-sm"
            placeholder={t("ask.input_ph")}
          />
          <button data-testid="ask-send-btn" onClick={send} disabled={sending || !input.trim()} className="pill-btn !px-4">
            <Send size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}
